import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { companyApi, F104, F1041, F1042, F1043Credit, F103, F1046 } from '../../../services/companyApi';

export type Mode = 'browse' | 'add' | 'edit';

const emptyCredit = (): F1043Credit => ({
  f1043_01: '', f1043_11: '',
  f1043_13: '', f1043_131: '', f1043_14: '', f1043_141: '',
  f1043_16: '', f1043_161: 0, f1043_162: '',
  f1043_17: '', f1043_171: 0, f1043_172: '',
  f1043_21: 0, f1043_22: 0, f1043_23: 0, f1043_24: 0,
  f1043_25: 0, f1043_26: 0, f1043_27: 0,
  f1043_31: 0, f1043_32: 0, f1043_35: 0, f1043_36: 0, f1043_37: 0,
});

const defaultAddresses = (code: string): F1041[] => [
  { f1041_01: code, f1041_02: 1, f1041_11: '公司', f1041_12: '', f1041_13: '', f1041_14: '' },
  { f1041_01: code, f1041_02: 2, f1041_11: '發票', f1041_12: '', f1041_13: '', f1041_14: '' },
  { f1041_01: code, f1041_02: 3, f1041_11: '工廠', f1041_12: '', f1041_13: '', f1041_14: '' },
];

const defaultContacts = (code: string): F1042[] => [
  { f1042_01: code, f1042_02: '', f1042_11: '負責人', f1042_12: '', f1042_13: '', f1042_14: '', f1042_15: '' },
  { f1042_01: code, f1042_02: '', f1042_11: '業務', f1042_12: '', f1042_13: '', f1042_14: '', f1042_15: '' },
  { f1042_01: code, f1042_02: '', f1042_11: '採購', f1042_12: '', f1042_13: '', f1042_14: '', f1042_15: '' },
  { f1042_01: code, f1042_02: '', f1042_11: '會計出納', f1042_12: '', f1042_13: '', f1042_14: '', f1042_15: '' },
];

export function useCompanyForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F104[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<F104 | null>(null);

  // Form fields
  const [code, setCode] = useState('');
  const [category, setCategory] = useState('C');
  const [shortName, setShortName] = useState('');
  const [fullName, setFullName] = useState('');
  const [enName, setEnName] = useState('');
  const [countryCode, setCountryCode] = useState('TWN');
  const [taxId, setTaxId] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [perfCustomer, setPerfCustomer] = useState(false);
  const [poRequired, setPoRequired] = useState(false);
  const [personInCharge, setPersonInCharge] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [oldCodeFlag, setOldCodeFlag] = useState(false);
  const [custCategory, setCustCategory] = useState('');
  const [payType, setPayType] = useState('01');
  const [taxRate, setTaxRate] = useState(0.05);
  const [remark, setRemark] = useState('');
  const [addresses, setAddresses] = useState<F1041[]>([]);
  const [contacts, setContacts] = useState<F1042[]>([]);
  const [credit, setCredit] = useState<F1043Credit>(emptyCredit());
  const [businessCards, setBusinessCards] = useState<F103[]>([]);
  const [orderCustomers, setOrderCustomers] = useState<F1046[]>([]);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async (keyword?: string) => {
    setLoading(true);
    try {
      const res = await companyApi.list(1, 500, keyword);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (code: string) => {
    try {
      const d = await companyApi.get(code);
      setDetail(d);
      return d;
    } catch {
      return null;
    }
  }, []);

  // Sync form fields from detail
  const syncFromDetail = useCallback((d: F104) => {
    setCode(d.f104_01);
    setCategory(d.f104_02);
    setShortName(d.f104_11);
    setFullName(d.f104_12);
    setEnName(d.f104_13);
    setCountryCode(d.f104_14);
    setTaxId(d.f104_21);
    setEmail(d.f104_22);
    setWebsite(d.f104_23);
    setPerfCustomer(d.f104_28);
    setPoRequired(d.f104_29 ?? false);
    setPersonInCharge(d.f104_30);
    setBankAccount(d.f104_34);
    setOldCodeFlag(d.f104_35);
    setCustCategory(d.f104_39 ?? '');
    setPayType(d.f104_41);
    setTaxRate(d.f104_42);
    setRemark(d.f104_24 ?? '');
    setAddresses(d.addresses || []);
    setContacts(d.contacts || []);
    setCredit(d.credit || emptyCredit());
    setBusinessCards(d.business_cards || []);
    setOrderCustomers(d.order_customers || []);
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') {
      loadDetail(current.f104_01).then(d => { if (d) syncFromDetail(d); });
    }
  }, [current, mode, loadDetail, syncFromDetail]);

  useEffect(() => {
    loadRecords().then(data => { if (data.length > 0) setCurrentIdx(0); });
  }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => {
    setMode('add');
    setCode(''); setCategory('C'); setShortName(''); setFullName(''); setEnName('');
    setCountryCode('TWN'); setTaxId(''); setEmail(''); setWebsite('');
    setPerfCustomer(false); setPoRequired(false); setPersonInCharge(''); setBankAccount('');
    setOldCodeFlag(false); setCustCategory(''); setPayType('01'); setTaxRate(0.05); setRemark('');
    setAddresses(defaultAddresses('')); setContacts(defaultContacts(''));
    setCredit(emptyCredit());
    setBusinessCards([]);
    setOrderCustomers([]);
  };

  const startEdit = () => {
    if (!detail) return;
    setMode('edit');
    syncFromDetail(detail);
  };

  const cancel = () => {
    setMode('browse');
    if (detail) syncFromDetail(detail);
  };

  const buildPayload = () => ({
    f104_02: category, f104_11: shortName, f104_12: fullName, f104_13: enName,
    f104_14: countryCode, f104_21: taxId, f104_22: email, f104_23: website, f104_24: remark,
    f104_28: perfCustomer, f104_29: poRequired, f104_30: personInCharge, f104_34: bankAccount,
    f104_35: oldCodeFlag, f104_39: custCategory, f104_41: payType, f104_42: taxRate,
    addresses, contacts, credit,
    business_cards: businessCards,
    order_customers: orderCustomers,
  });

  const save = async () => {
    try {
      if (mode === 'add') {
        await companyApi.create({ f104_01: code, ...buildPayload() });
        message.success('新增成功');
      } else {
        await companyApi.update(current!.f104_01, buildPayload());
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetCode = mode === 'add' ? code : current!.f104_01;
      const idx = data.findIndex((r: F104) => r.f104_01 === targetCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  const doCopy = async (newCode: string) => {
    if (!current) return;
    try {
      await companyApi.copy({ new_code: newCode, from_code: current.f104_01 });
      message.success('複製成功');
      const data = await loadRecords();
      const idx = data.findIndex((r: F104) => r.f104_01 === newCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) vErrs.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '複製失敗');
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await companyApi.delete(current.f104_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1));
      else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const quickSearch = (text: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f104_01 >= text);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading, records, current, currentIdx, detail,
    code, setCode, category, setCategory, shortName, setShortName,
    fullName, setFullName, enName, setEnName, countryCode, setCountryCode,
    taxId, setTaxId, email, setEmail, website, setWebsite,
    perfCustomer, setPerfCustomer, poRequired, setPoRequired,
    personInCharge, setPersonInCharge,
    bankAccount, setBankAccount, oldCodeFlag, setOldCodeFlag,
    custCategory, setCustCategory, payType, setPayType, taxRate, setTaxRate, remark, setRemark,
    addresses, setAddresses, contacts, setContacts, credit, setCredit,
    businessCards, setBusinessCards, orderCustomers, setOrderCustomers,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doCopy, doDelete, quickSearch,
  };
}
